import type { DeliverSchemaTypes } from "@bfc-software/api-schemas";
import {
  EmptyState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@bfc-software/pallet";
// eslint-disable-next-line no-restricted-imports -- follow-up: use useCurrentLocalization()
import type { TFunction } from "i18next";

import { formatCount } from "../-data/format";

import { ScanBar } from "./scan-bar";
import styles from "./routes-table.module.css";

type DashDriver = DeliverSchemaTypes.components["schemas"]["DashDriver"];

const COLUMN_COUNT = 8;

type Props = {
  rows: ReadonlyArray<DashDriver>;
  lang: string;
  t: TFunction;
};

export function DriversTable({ rows, lang, t }: Props) {
  const none = t("none");
  const n = (value: number) => formatCount(value, lang);

  return (
    <Table
      aria-label={t("drivers.ariaLabel")}
      className={rows.length < 1 ? "min-h-full" : ""}
      isResizable={false}
    >
      <TableHead>
        <TableHeaderCell isRowHeader={true}>
          {t("drivers.driver")}
        </TableHeaderCell>
        <TableHeaderCell>{t("drivers.routes")}</TableHeaderCell>
        <TableHeaderCell>{t("drivers.stops")}</TableHeaderCell>
        <TableHeaderCell>{t("drivers.delivered")}</TableHeaderCell>
        <TableHeaderCell>{t("drivers.scanned")}</TableHeaderCell>
        <TableHeaderCell>{t("drivers.nonScanned")}</TableHeaderCell>
        <TableHeaderCell>{t("drivers.scanPct")}</TableHeaderCell>
        <TableHeaderCell>{t("drivers.pph")}</TableHeaderCell>
      </TableHead>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={COLUMN_COUNT}>
              <EmptyState heading={t("list.emptyDrivers")} />
            </TableCell>
          </TableRow>
        ) : (
          rows.map((row) => (
            <TableRow key={row.driver} data-test-role="driver-row">
              <TableCell>
                <div className={styles.stacked}>
                  <span className={styles.primary}>
                    {row.driverName || none}
                  </span>
                  <span className={styles.secondary}>{row.driver}</span>
                </div>
              </TableCell>
              <TableCell>{n(row.routes)}</TableCell>
              <TableCell>
                {n(row.stopsDelivered)} / {n(row.stops)}
              </TableCell>
              <TableCell>{n(row.piecesDelivered)}</TableCell>
              <TableCell>{n(row.piecesScanned)}</TableCell>
              <TableCell>{n(row.piecesNonScanned)}</TableCell>
              <TableCell>
                <ScanBar value={row.scanPct} lang={lang} none={none} />
              </TableCell>
              <TableCell>{n(row.pph)}</TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}
